import type { FC, ReactNode } from 'react'
import type { WorkItem } from '@/types'

import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import MediaImage from '@/components/landing/media-image'
import Sparkle from '@/components/landing/decor/sparkle'
import PatternArt from '@/components/landing/decor/pattern-art'
import { WORKS_COPY } from '@/constants/content'

interface WorkLogCardProps {
    work: WorkItem
    // Position in the whole archive, shown as LOG.001 and the same on every page.
    number: number
    className?: string
}

export const formatLogNumber = (number: number): string => `LOG.${String(number).padStart(3, '0')}`

// A slight, steady tilt per card (from its log number), so a grid reads like pinned record photos.
const TILTS = ['-rotate-[1.2deg]', 'rotate-[0.8deg]', '-rotate-[0.4deg]', 'rotate-[1deg]', '-rotate-[0.8deg]', 'rotate-[0.5deg]']

// One activity-log entry as a polaroid: the work's own image in a cream frame with a strip of
// tape, and the record written underneath (log number, year, category, title, client, role).
// Works without an image get a quiet geometric placeholder. The whole card opens the record.
const WorkLogCard: FC<WorkLogCardProps> = ({ work, number, className }): ReactNode => {
    return (
        <article
            className={cn(
                'group/work relative flex h-full flex-col rounded-[3px] bg-[#fbf8f0] p-2.5 pb-4 shadow-[0_22px_44px_-28px_rgba(29,42,51,0.55)] ring-1 ring-ink/10 transition-[rotate,translate,box-shadow] duration-700 ease-[var(--ease-cinema)] hover:-translate-y-1 hover:rotate-0 hover:shadow-[0_30px_56px_-30px_rgba(29,42,51,0.6)] sm:p-3 sm:pb-5',
                TILTS[(number - 1) % TILTS.length],
                className
            )}
        >
            <span aria-hidden='true' className='absolute -top-2 left-1/2 z-10 h-4 w-14 -translate-x-1/2 -rotate-3 bg-[#eadfbf]/85 shadow-sm' />

            <div className='relative aspect-[16/10] overflow-hidden rounded-[2px] bg-paper'>
                {work.image
                    ? (
                        <MediaImage
                            src={work.image}
                            alt={work.imageAlt ?? work.title}
                            sizes='(min-width: 1024px) 26vw, (min-width: 640px) 46vw, 92vw'
                            className='object-cover transition-transform duration-[1200ms] ease-[var(--ease-cinema)] group-hover/work:scale-[1.04]'
                        />
                    )
                    : (
                        <div className='absolute inset-0'>
                            <PatternArt seed={work.id} className='absolute inset-0 h-full w-full opacity-70' />
                            <span className='absolute inset-0 m-auto flex h-fit w-fit items-center gap-1.5 rounded-full bg-mist/90 px-3 py-1.5 font-mono text-[9px] tracking-[0.24em] text-ink-soft shadow-sm ring-1 ring-ink/10'>
                                <Sparkle className='size-2.5 text-gold' />
                                RECORD
                            </span>
                        </div>
                    )}
                <span className='absolute left-2 top-2 rounded-full bg-mist/90 px-2 py-0.5 font-mono text-[9px] tracking-[0.2em] text-ink shadow-sm'>
                    {formatLogNumber(number)}
                </span>
            </div>

            <div className='flex flex-1 flex-col gap-1.5 px-1 pt-3.5'>
                <p className='flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10px] tracking-[0.18em] text-ink-soft'>
                    {work.year && <span data-mdlfy-cms={`works:${work.id}:year`}>{work.year}</span>}
                    <span className='inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-sans text-[10px] tracking-[0.08em] text-ink/80 ring-1 ring-ink/15'>
                        <Sparkle className='size-2 shrink-0 text-gold' />
                        <span data-mdlfy-cms={`works:${work.id}:category`}>{work.category}</span>
                    </span>
                </p>

                <h3 data-mdlfy-cms={`works:${work.id}:title`} className='line-clamp-2 text-sm leading-[1.7] tracking-[0.04em] text-ink'>
                    {work.title}
                </h3>

                {(work.client || work.role) && (
                    <p className='flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] leading-[1.7] tracking-[0.04em] text-ink/65'>
                        {work.client && <span data-mdlfy-cms={`works:${work.id}:client`}>{work.client}</span>}
                        {work.client && work.role && <span aria-hidden='true' className='text-ink/30'>/</span>}
                        {work.role && <span data-mdlfy-cms={`works:${work.id}:role`}>{work.role}</span>}
                    </p>
                )}

                {work.url && (
                    <a
                        href={work.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='mt-auto self-end rounded-full p-1 text-ink/50 transition-colors duration-500 after:absolute after:inset-0 after:rounded-[3px] group-hover/work:text-gold-deep focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                    >
                        <ArrowUpRight className='size-4' />
                        <span className='sr-only'>{`${work.title}を${WORKS_COPY.open}（新しいタブで開きます）`}</span>
                    </a>
                )}
            </div>
        </article>
    )
}

export default WorkLogCard
